/**
 * HandUI - The player's hand display component.
 *
 * Responsibilities:
 * - Render up to 8 cards (minions) from the player's hand
 * - Display each card's stats (HP, attack, level) and abilities
 * - React to backend state updates (GameStateUpdate, RoundStarted, etc.)
 * - Provide reactive render data for UI binding
 * - Indicate full-hand state (8 cards)
 *
 * The HandUI reads state from AppShell (which holds the authoritative
 * GameStateService) and computes display data for the hand overlay.
 * The backend is authoritative; the hand is always what the server says.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import { MinionInHandData } from '../../shared/messages/MessageContracts';
import { AbilityRegistry, AbilityTriggerType } from '../../shared/models/AbilityRegistry';
import { AbilityDisplayService, AbilityDisplayData, MinionAbilityDisplayData } from '../services/AbilityDisplayService';
import { resolveAbilityNames, type AbilityDefinitionSummary } from '../../shared/models/UpgradePath';
import { DISCOUNT_ABILITY_ID, DISCOUNT_COST_REDUCTION } from '../../shared/models/AbilityConstants';
import { GameConfig } from '../../config/gameConfig';
import { GAME_ABILITY_DEFINITIONS } from '../../services/GameAbilities';
import { RUNE_DEFINITIONS, getRuneById } from '../../shared/models/RuneDefinitions';
import { UPGRADED_SPELL_ID } from '../../shared/models/SpellDefinitions';
import {
  getEffectiveRuneCap,
  getRuneGoldCost,
  isRuneFeastActive,
} from '../../shared/models/RunePurchaseRules';

const ABILITY_SUMMARIES: AbilityDefinitionSummary[] = GAME_ABILITY_DEFINITIONS.map(
  (def) => ({ id: def.id, name: def.name, description: def.description, types: def.types, availableAtLevel: def.availableAtLevel })
);

// ─── Hand Card Render Data ──────────────────────────────────────────────────

/**
 * Render data for a single card in the hand.
 */
export interface HandCardRenderData {
  /** Unique instance ID for this minion copy. */
  instanceId: string;
  /** The base minion definition ID. */
  baseMinionId: string;
  /** Current HP of the minion. */
  currentHp: number;
  /** Attack value of the minion. */
  attack: number;
  /** The minion's type classification (e.g., "warrior", "mage"). */
  minionType: string;
  /** Number of upgrades applied (0 = base). */
  upgradeCount: number;
  /** Computed level: upgradeCount + 1. */
  level: number;
  /** Ability IDs present on this minion. */
  abilities: string[];
  /** Human-readable ability names (resolved from shared definitions). */
  abilityNames: string[];
  /** Short descriptions of each ability (matched by index to abilityNames). */
  abilityDescriptions: string[];
  /**
   * ENRICHMENT (spell, task 197): the card's per-turn counter (the dynamic
   * description shows 1 + this gold). Absent on non-Enrichment cards.
   */
  enrichmentTurns?: number;
  /**
   * UPGRADED (spell, task 196): the CARD OWNER's Upgraded cast count (the
   * dynamic description shows the buff the next cast would give). Absent
   * on non-Upgraded cards / when unknown.
   */
  ownerUpgradedCasts?: number;
  /** Whether the card has any abilities. */
  hasAbilities: boolean;
}

/**
 * Render data for the entire hand.
 */
export interface HandUIRenderData {
  /** Whether the hand UI should be visible (player is in a match and in preparation). */
  isVisible: boolean;

  /** Array of cards in hand, in server-ordered sequence. */
  cards: HandCardRenderData[];

  /** Number of cards currently in hand. */
  handSize: number;

  /** Whether the hand is at maximum capacity (8 cards). */
  isFull: boolean;

  /** Maximum hand size (from game config). */
  maxHandSize: number;

  /** Whether the hand is empty. */
  isEmpty: boolean;

  /**
   * The cost of buying a new card right now (mirror of the server's
   * authoritative computation: base 5 gold, -1 per friendly Discount minion
   * in play, never below the configured minimum of 2). Used for the Buy
   * button label — the server re-validates everything on the request.
   */
  purchaseCost: number;

  /** Number of friendly Discount minions in play driving the reduction. */
  discountCount: number;

  /**
   * Whether the Buy button should be enabled: (unlocked) preparation phase,
   * enough gold for the current cost, and a free hand slot. Purely
   * cosmetic — the server is authoritative.
   */
  canBuy: boolean;

  // ── Rune shop button state ─────────────────────────────────────────────
  // The server is authoritative for the purchased runes (PlayerData.runes.purchased);
  // rune content (names/costs/art) comes from the shared RUNE_DEFINITIONS
  // catalogue (the same source that seeds the server's runes table). The
  // values below are display mirrors only.

  /** The player's ACTIVE runes — the ones already purchased this game. */
  activeRunes: string[];

  /**
   * The player's PERSONAL rune purchases (the "(x/N)" counter's x). In a
   * COOP match `activeRunes` is the SHARED team union (every team rune is
   * owned by everyone), while the cap budget is per player — so the button
   * counts this list. Regular matches: identical to `activeRunes` (legacy
   * servers without the field fall back to it).
   */
  personalRunes: string[];

  /**
   * The player's EFFECTIVE per-game rune purchase cap right now: the base
   * cap (GameConfig.maxRunesPurchasable = 3) plus one per RuneKeeper minion
   * in play (stacking). Displayed in the button's "(x/N)" counter — the
   * server is authoritative, this is a mirror. (Can sit BELOW the number of
   * activeRunes — the "(4/3)" state after a RuneKeeper leaves; the runes
   * stay, the counter just shows the dropped limit.)
   */
  runeCap: number;

  /**
   * Whether a RuneFeast minion is in play (all rune prices show the 25%
   * discount in the shop). Display mirror only — the server prices the
   * purchase authoritatively.
   */
  runeFeastActive: boolean;

  /**
   * Whether the 🪬 Buy Rune button should be VISIBLE: in a match, (unlocked)
   * preparation phase, and at least one rune is still unpurchased (the shop
   * is open). Purely cosmetic — the server re-validates everything on the
   * request.
   */
  runeShopVisible: boolean;

  /**
   * Whether the 🪬 Buy Rune button should be enabled: the shop is visible
   * and the preparation phase is not locked. (Affordability is per-rune in
   * the shop — the button itself is always enabled while preparation is
   * unlocked.)
   */
  runeShopEnabled: boolean;
}

/**
 * Callback invoked when the render data changes.
 */
export type HandUIRenderCallback = (data: HandUIRenderData) => void;

/**
 * Callback invoked when a card is selected (for interaction tasks).
 */
export type HandCardSelectedCallback = (card: HandCardRenderData) => void;

/**
 * Render data for a card inspection, including full ability display details.
 */
export interface InspectedCardData extends HandCardRenderData {
  /** Full ability display data (name, description, trigger type for each ability). */
  abilityDisplay: MinionAbilityDisplayData;

  /** Human-readable summary of abilities for tooltip/brief display. */
  abilitySummary: string;

  /** Abilities grouped by trigger type. */
  abilitiesByTrigger: Map<AbilityTriggerType, AbilityDisplayData[]>;
}

// ─── Constants ──────────────────────────────────────────────────────────────

/** Maximum hand size as defined in game configuration. */
const MAX_HAND_SIZE = 8;

// ─── Hand UI ────────────────────────────────────────────────────────────────

export class HandUI {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: HandUIRenderCallback[] = [];
  private _cardSelectedCallbacks: HandCardSelectedCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  private _abilityDisplayService: AbilityDisplayService;

  constructor(appShell: AppShell, abilityRegistry?: AbilityRegistry) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;

    // Use provided registry or create a default one
    const registry = abilityRegistry ?? new AbilityRegistry();
    this._abilityDisplayService = new AbilityDisplayService(registry);
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data (computed fresh from AppShell state). */
  get renderData(): HandUIRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the hand UI is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the hand UI and start listening for state changes.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    // Listen for app shell events to trigger render updates
    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    // Initial render
    this._notifyRenderCallbacks();
  }

  /**
   * Hide the hand UI and stop listening for state changes.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: HandUIRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to card selection events.
   * Returns an unsubscribe function.
   */
  onCardSelected(callback: HandCardSelectedCallback): () => void {
    this._cardSelectedCallbacks.push(callback);
    return () => {
      const index = this._cardSelectedCallbacks.indexOf(callback);
      if (index !== -1) {
        this._cardSelectedCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Player Actions ──────────────────────────────────────────────────────

  /**
   * Inspect a card by its instance ID to get full ability display data.
   *
   * Resolves each ability ID on the card using the AbilityRegistry to produce
   * human-readable name, description, and trigger type. Unknown abilities are
   * represented with a fallback display showing the raw ID.
   *
   * @param instanceId - The instance ID of the card to inspect.
   * @returns Inspected card data with full ability details, or null if not found.
   */
  inspectCard(instanceId: string): InspectedCardData | null {
    const renderData = this._buildRenderData();
    const card = renderData.cards.find((c) => c.instanceId === instanceId) ?? null;
    if (!card) {
      return null;
    }

    const abilityDisplay = this._abilityDisplayService.resolveAbilities(card.abilities);
    const abilitySummary = this._abilityDisplayService.getAbilitySummary(card.abilities);
    const abilitiesByTrigger = this._abilityDisplayService.groupByTriggerType(card.abilities);

    return {
      ...card,
      abilityDisplay,
      abilitySummary,
      abilitiesByTrigger,
    };
  }

  /**
   * Select a card by its instance ID.
   * Notifies card-selected callbacks with the card data.
   * Returns the card data if found, null otherwise.
   */
  selectCard(instanceId: string): HandCardRenderData | null {
    const renderData = this._buildRenderData();
    const card = renderData.cards.find((c) => c.instanceId === instanceId) ?? null;
    if (card) {
      this._notifyCardSelectedCallbacks(card);
    }
    return card;
  }

  /**
   * Play a card from hand to the battlefield.
   *
   * Validates that the card exists in the current hand and that a match
   * is active, then sends a PlayMinionRequest to the backend through AppShell.
   * The backend is authoritative — it validates the action and produces
   * the resulting state. The UI reconciles when the GameStateUpdate arrives.
   *
   * @param minionInstanceId - The instance ID of the card to play.
   * @param targetPosition - Optional battlefield position (0-indexed) to place the minion.
   * @returns true if the play request was sent successfully, false otherwise.
   */
  playCard(minionInstanceId: string, targetPosition?: number): boolean {
    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: must be in preparation phase and not locked
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    const playerPhase = currentPlayer?.phase ?? matchState.phase;
    if (playerPhase !== 'preparation' || matchState.preparationLocked) {
      return false;
    }

    // Validate: card must exist in current hand
    const renderData = this._buildRenderData();
    const card = renderData.cards.find((c) => c.instanceId === minionInstanceId) ?? null;
    if (!card) {
      return false;
    }

    // Get match ID from current state
    const matchId = this._appShell.gameState.match.matchId;
    if (!matchId) {
      return false;
    }

    // Send play request through AppShell (which sends to backend via WebSocket)
    return this._appShell.playMinion(matchId, minionInstanceId, targetPosition);
  }

  /**
   * Cast a spell card from the hand: the spell's effect goes into play and
   * the card despawns (it never lands on the battlefield — spells are cast,
   * minions are played).
   *
   * Validates that the card exists in the current hand (and is a spell) and
   * that a match is active and in the (unlocked) preparation phase, then
   * sends a PlaySpellRequest to the backend through AppShell. The backend
   * is authoritative — it validates the cast (target legality for targeted
   * spells) and produces the resulting state. The UI reconciles when the
   * GameStateUpdate arrives.
   *
   * @param spellInstanceId - The instance ID of the spell card to cast.
   * @param targetInstanceId - The target minion ('minion'-target spells only).
   * @returns true if the cast request was sent successfully, false otherwise.
   */
  playSpellCard(spellInstanceId: string, targetInstanceId?: string): boolean {
    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: must be in preparation phase and not locked
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    const playerPhase = currentPlayer?.phase ?? matchState.phase;
    if (playerPhase !== 'preparation' || matchState.preparationLocked) {
      return false;
    }

    // Validate: card must exist in current hand and be a spell
    const renderData = this._buildRenderData();
    const card = renderData.cards.find((c) => c.instanceId === spellInstanceId) ?? null;
    if (!card || card.minionType !== 'spell') {
      return false;
    }

    // Get match ID from current state
    const matchId = this._appShell.gameState.match.matchId;
    if (!matchId) {
      return false;
    }

    // Send cast request through AppShell (which sends to backend via WebSocket)
    return this._appShell.playSpell(matchId, spellInstanceId, targetInstanceId);
  }

  /**
   * Sell a card from the hand (drag-to-sell from the top-half sell zone).
   *
   * Validates that the card exists in the current hand and that a match is
   * active and in the (unlocked) preparation phase, then sends a
   * SellMinionRequest to the backend through AppShell. The backend is
   * authoritative — it validates the sale and produces the resulting state.
   * The UI reconciles when the GameStateUpdate arrives.
   *
   * Selling is a REMOVAL, not a play: the card never counts as played (no
   * play effects; the Undead Improved counter is unaffected). The server
   * enforces this authoritatively.
   *
   * The expected sale value is the card's level (upgradeCount + 1) gold.
   *
   * @param minionInstanceId - The instance ID of the card to sell.
   * @returns true if the sell request was sent successfully, false otherwise.
   */
  sellCard(minionInstanceId: string): boolean {
    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: must be in preparation phase and not locked
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    const playerPhase = currentPlayer?.phase ?? matchState.phase;
    if (playerPhase !== 'preparation' || matchState.preparationLocked) {
      return false;
    }

    // Validate: card must exist in current hand
    const renderData = this._buildRenderData();
    const card = renderData.cards.find((c) => c.instanceId === minionInstanceId) ?? null;
    if (!card) {
      return false;
    }

    // Get match ID from current state
    const matchId = this._appShell.gameState.match.matchId;
    if (!matchId) {
      return false;
    }

    // Send sell request through AppShell (which sends to backend via WebSocket)
    return this._appShell.sellMinion(matchId, minionInstanceId);
  }

  /**
   * Buy a new (random) card into the hand for the current cost.
   *
   * Validates that a match is active, we are in the (unlocked) preparation
   * phase, the hand has a free slot and the player can afford the current
   * (Discount-reduced) cost, then sends a BuyCardRequest to the backend
   * through AppShell. The backend is authoritative — it recomputes the cost
   * and re-validates gold + hand capacity. The UI reconciles when the
   * GameStateUpdate arrives.
   *
   * @returns true if the buy request was sent successfully, false otherwise.
   */
  buyCard(): boolean {
    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: must be in preparation phase and not locked
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    const playerPhase = currentPlayer?.phase ?? matchState.phase;
    if (playerPhase !== 'preparation' || matchState.preparationLocked) {
      return false;
    }

    // Validate: affordable + hand space (mirrors the button's enabled state)
    const renderData = this._buildRenderData();
    if (!renderData.canBuy) {
      return false;
    }

    // Get match ID from current state
    const matchId = this._appShell.gameState.match.matchId;
    if (!matchId) {
      return false;
    }

    // Send buy request through AppShell (which sends to backend via WebSocket)
    return this._appShell.buyCard(matchId);
  }

  /**
   * Buy a SPECIFIC rune from the in-game rune shop (the user's choice —
   * the shop overlay lists every rune in the shared catalogue with its
   * gold cost).
   *
   * Validates that a match is active, we are in the (unlocked) preparation
   * phase, the rune is known and not yet purchased, and the player can
   * afford its catalogue cost, then sends a BuyRuneRequest to the backend
   * through AppShell. The backend is authoritative — it re-validates the
   * rune, the ownership and the gold. The UI reconciles when the
   * GameStateUpdate arrives (PlayerData.runes.purchased).
   *
   * @param runeId The rune to buy (a shared-catalogue rune ID).
   * @returns true if the buy-rune request was sent successfully, false otherwise.
   */
  buyRune(runeId: string): boolean {
    // Validate: must be in a match
    if (!this._appShell.isInMatch) {
      return false;
    }

    // Validate: must be in preparation phase and not locked
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    const playerPhase = currentPlayer?.phase ?? matchState.phase;
    if (playerPhase !== 'preparation' || matchState.preparationLocked) {
      return false;
    }

    // Validate: the rune is known, not yet owned, and affordable (mirrors
    // the shop row state — the server re-validates all of it).
    const def = getRuneById(runeId);
    if (!def) return false;
    const purchased = currentPlayer?.runes?.purchased ?? [];
    if (purchased.includes(def.id)) return false;
    if (!currentPlayer || currentPlayer.gold < def.goldCost) return false;

    // Get match ID from current state
    const matchId = this._appShell.gameState.match.matchId;
    if (!matchId) {
      return false;
    }

    // Send buy-rune request through AppShell (which sends to backend via WebSocket)
    return this._appShell.buyRune(matchId, runeId);
  }

  // ─── Internal Methods ────────────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        // Update render data on any game state change
        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        // Hide when navigating away from match screen
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyCardSelectedCallbacks(card: HandCardRenderData): void {
    for (const callback of this._cardSelectedCallbacks) {
      callback(card);
    }
  }

  private _buildRenderData(): HandUIRenderData {
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;

    // Find current player's data
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;

    // Extract hand cards from the authoritative backend state
    const handCards: MinionInHandData[] = currentPlayer?.hand ?? [];

    // Transform into render data
    const cards: HandCardRenderData[] = handCards.map((card) => {
      const names = resolveAbilityNames(card.abilities, ABILITY_SUMMARIES);
      const descs = card.abilities.map((id) => {
        const def = ABILITY_SUMMARIES.find((d) => d.id === id);
        return def ? def.description : '';
      });
      return {
        instanceId: card.instanceId,
        baseMinionId: card.baseMinionId,
        currentHp: card.currentHp,
        attack: card.attack,
        minionType: card.minionType,
        upgradeCount: card.upgradeCount,
        level: card.upgradeCount + 1,
        abilities: [...card.abilities],
        abilityNames: names,
        abilityDescriptions: descs,
        hasAbilities: card.abilities.length > 0,
        // ENRICHMENT (task 197): the per-card counter (dynamic
        // description: 1 + turns in hand gold).
        ...(card.enrichmentTurns !== undefined
          ? { enrichmentTurns: card.enrichmentTurns }
          : {}),
        // UPGRADED (task 196): the OWNER's cast count (dynamic
        // description: the buff the next cast would give).
        ...(card.baseMinionId === UPGRADED_SPELL_ID && currentPlayer
          ? { ownerUpgradedCasts: currentPlayer.upgradedSpellCasts ?? 0 }
          : {}),
      };
    });

    const handSize = cards.length;
    const isFull = handSize >= MAX_HAND_SIZE;
    const isEmpty = handSize === 0;

    // Buy-a-card data: mirror of the server's authoritative cost computation
    // (GameSessionService.getCardPurchaseCost) — base cost minus one per
    // friendly Discount minion IN PLAY, clamped to the configured minimum.
    const discountCount = (currentPlayer?.battlefield ?? []).filter(
      (m) => m.abilities.includes(DISCOUNT_ABILITY_ID)
    ).length;
    const purchaseCost = Math.max(
      GameConfig.cardPurchaseMinCost,
      GameConfig.cardPurchaseCost - discountCount * DISCOUNT_COST_REDUCTION
    );
    const inPreparation =
      (currentPlayer?.phase ?? matchState.phase) === 'preparation' &&
      !matchState.preparationLocked;
    const canBuy =
      !!currentPlayer && inPreparation && !isFull && currentPlayer.gold >= purchaseCost;

    // Rune-shop data: the purchased list is the server's authoritative
    // state (PlayerData.runes.purchased); rune content (names/costs/art)
    // comes from the shared RUNE_DEFINITIONS catalogue (the same source
    // that seeds the server's runes table). Missing rune state (older
    // servers / mid-reconnect) → no active runes (defensive, never crash
    // the render).
    const purchasedRunes = [...(currentPlayer?.runes?.purchased ?? [])];
    // COOP: the personal list (own cap budget) — falls back to the shared
    // union for regular matches / legacy servers.
    const personalRunes = [
      ...(currentPlayer?.runes?.personalPurchased ?? purchasedRunes),
    ];
    const prepPhase = (currentPlayer?.phase ?? matchState.phase) === 'preparation';
    // Mirror of the server's authoritative rune-shopping modifiers
    // (RunePurchaseRules, read over the broadcast battlefield — the same
    // in-play-only convention the server uses): the effective cap (base +
    // one per RuneKeeper in play) and whether the 25% RuneFeast discount is
    // active. Missing battlefield (older server / mid-reconnect) → base
    // cap, no discount (defensive, never crash the render).
    const inPlay = (currentPlayer?.battlefield ?? []).map((m) => m.abilities);
    const runeCap = getEffectiveRuneCap(inPlay);
    const runeFeastActive = isRuneFeastActive(inPlay);
    // The shop stays open for the WHOLE preparation phase (as long as the
    // catalogue is not fully owned): the cap is DYNAMIC — a RuneKeeper can
    // still be played this game, and at/over the cap the button shows the
    // "(x/N)" state (e.g. "(4/3)" after a RuneKeeper leaves) while the
    // open shop displays every row as locked.
    const runeShopVisible =
      this._appShell.isInMatch &&
      prepPhase &&
      purchasedRunes.length < RUNE_DEFINITIONS.length; // something left to own
    const runeShopEnabled = runeShopVisible && !matchState.preparationLocked;

    // Hand is visible when in a match (regardless of phase - the view layer
    // may choose to hide cards during combat, but the data is always available)
    const isVisible = this._appShell.isInMatch;

    return {
      isVisible,
      cards,
      handSize,
      isFull,
      maxHandSize: MAX_HAND_SIZE,
      isEmpty,
      purchaseCost,
      discountCount,
      canBuy,
      activeRunes: purchasedRunes,
      personalRunes,
      runeCap,
      runeFeastActive,
      runeShopVisible,
      runeShopEnabled,
    };
  }
}
